import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';

// Reduced timeout: if loading takes more than 4s with no user, show fallback
const SESSION_TIMEOUT_MS = 4000;

const ProtectedRoute = ({ children }) => {
  const { user, loading, sessionReady, contextError, refreshContext, lastBootstrapPhase } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      if (loading) {
        console.log('[GearControl][PHASE] GUARD_TIMEOUT');
        setTimedOut(true);
      }
    }, SESSION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // If we have a user, render children immediately — don't wait for profile
  if (user) {
    const isSoftError = contextError === 'Sesión iniciada. Cargando datos adicionales...';

    if (contextError && !isSoftError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-md">
            <Icon name="AlertCircle" className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Problema de configuración</h2>
            <p className="text-gray-600 mb-4">{contextError}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={refreshContext}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors"
              >
                <Icon name="RefreshCw" size={16} color="white" />
                Reintentar
              </button>
              <a
                href="/signin"
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors"
              >
                Cerrar sesión
              </a>
            </div>
          </div>
        </div>
      );
    }

    // GUARD_PASS — session confirmed, render children immediately
    console.log('[GearControl][PHASE] GUARD_PASS');

    if (isSoftError) {
      return (
        <>
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-center text-sm text-blue-700 flex items-center justify-center gap-2">
            <Icon name="Loader2" size={14} className="animate-spin" />
            Sesión iniciada. Cargando datos adicionales...
            <button
              onClick={refreshContext}
              className="underline hover:no-underline ml-2 font-medium"
            >
              Reintentar
            </button>
          </div>
          {children}
        </>
      );
    }

    return children;
  }

  // No user yet — check if we timed out
  if (timedOut && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md">
          <Icon name="Clock" className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Sesión iniciada</h2>
          <p className="text-gray-500 mb-4">Cargando datos adicionales...</p>
          <p className="text-gray-400 text-xs mb-4">Fase: {lastBootstrapPhase || 'GUARD_TIMEOUT'}</p>
          <button
            onClick={() => { setTimedOut(false); refreshContext?.(); }}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors mx-auto"
          >
            <Icon name="RefreshCw" size={16} color="white" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Still loading, no user yet — brief spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Icon name="Loader2" className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Cargando sesión...</p>
          <p className="text-gray-400 text-sm mt-1">Si tarda mucho, recarga la página</p>
        </div>
      </div>
    );
  }

  // Not loading, no user — redirect to sign in
  return <Navigate to="/signin" replace />;
};

export default ProtectedRoute;